import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getFormById } from "../api/Api";
import Loader from "../Loader/Loader";
import logo from "../Assets/logo.png";


const PrintSingleForm = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await getFormById(id);
        if (response.success) {
          setFormData(response.data);
        } else {
          console.error("Error fetching form:", response);
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFormData();
    }
  }, [id]);

  useEffect(() => {
    if (!loading && formData) {
      const timeoutId = setTimeout(() => {
        window.print();
        // Optional: Close the window after printing or if printing is cancelled
        window.onafterprint = () => {
          // This ensures the window closes after the print dialog is handled
          // but might not work in all browsers due to security restrictions.
          // Consider navigating back or showing a "Print complete" message instead.
          window.close(); 
        };
      }, 500); 
      return () => clearTimeout(timeoutId);
    }
  }, [loading, formData]);

  if (loading) {
    return <Loader />;
  }

  if (!formData) {
    return <div className="text-center mt-8">Form not found.</div>;
  }

  const characteristics = [
    "Aggressive", "Friendly", "Moody", "Clumsy", "Dependent", "Fearful", "Quiet",
    "Good-natured", "Even-tempered", "Impulsive", "Shy", "Sympathetic", "Stubborn", "Attentive",
  ];

  return (
    <div ref={contentRef} className="max-w-screen-xl poppins-bold mx-auto p-8 print-container">
      {/* Logo and Branch Address */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center border-b border-[#16bab1] mb-6">
        <div className="flex flex-col items-center lg:items-start">
          <img
            src={logo}
            alt="Logo"
            className="h-56 w-72 object-contain mx-auto lg:mx-0"
          />
          {formData.branch && formData.branch.address && (
            <p className="text-sm text-gray-600 mt-2 text-center lg:text-left">{formData.branch.address}</p>
          )}
        </div>
        <div className="flex flex-col w-full lg:ml-8">
          <h1 className="text-4xl text-[#16bab1] font-semibold mb-4 text-center lg:text-left">
            Admission Form
          </h1>
          <div className="flex flex-col lg:flex-row lg:space-x-8">
            <div className="mt-2 w-full lg:w-auto">
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700"
              >
                Date of Admission:
              </label>
              <input
                type="date"
                id="date"
                value={formData.dateOfAdmission || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
            <div className="mt-4 mb-3 lg:mt-2 w-full lg:w-auto">
              <label
                htmlFor="admission"
                className="block text-sm font-medium text-gray-700"
              >
                Admission For:
              </label>
              <div className="flex flex-wrap gap-4 mt-2">
                <div
                  className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
                >
                  {formData.admissionFor || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="border border-gray-300 p-6 rounded-lg mb-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-[#16bab1]">
            Particulars of the Child
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Full Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Full Name:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.fullName || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Surname */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Surname:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.surname || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Name Used at Home */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Name Used at Home:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.nameUsedAtHome || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Nationality */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Nationality:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.nationality || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Date of Birth */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Date of Birth:
              </label>
              <input
                type="date"
                value={formData.particularsOfChild?.dob || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Age */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Age:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.age || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Gender Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Gender:
              </label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfChild?.gender || 'N/A'}
              </div>
            </div>

            {/* House No. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                House No.:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.houseNo || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Name of the Apartment/Building/House */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Apartment/Building Name:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.buildingName || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Street Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Street Name:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.streetName || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Town/City */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Town/City:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.city || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Pincode */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Pincode:
              </label>
              <input
                type="text"
                value={formData.particularsOfChild?.pincode || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>

            {/* Language(s) Spoken at Home */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Language(s) Spoken at Home
              </label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfChild?.language || 'N/A'}
              </div>

              {formData.particularsOfChild?.language === "Other" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Please specify:
                  </label>
                  <input
                    type="text"
                    value={formData.particularsOfChild?.otherLanguage || ""}
                    className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Particulars of Parents / Guardian */}
        <div className="mb-4">
          <h2 className="text-2xl font-semibold mb-4 text-[#16bab1]">Father / Guardian</h2>
          <div className="grid grid-cols-2 gap-4 items-start">
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Name:</label>
              <input
                type="text"
                value={formData.particularsOfParents?.FatherName || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Educational Qualification:</label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfParents?.FatherQualification || 'N/A'}
              </div>
            </div>
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Occupation:</label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfParents?.FatherOccupation || 'N/A'}
              </div>
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Name of the Organisation:</label>
              <input
                type="text"
                value={formData.particularsOfParents?.FatherOrganisationName || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
            <div className="col-span-4 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Tel No.:</label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <input type="text" placeholder="[STD]" className="px-4 py-2 border border-gray-300 rounded-md w-full" value={formData.particularsOfParents?.FatherSTDCodeRes || ""} readOnly />
                <input type="text" placeholder="[Res]" className="px-4 py-2 border border-gray-300 rounded-md w-full" value={formData.particularsOfParents?.FatherTelRes || ""} readOnly />
                <input type="text" placeholder="[STD]" className="px-4 py-2 border border-gray-300 rounded-md w-full" value={formData.particularsOfParents?.FatherSTDCodeOff || ""} readOnly />
                <input type="text" placeholder="[Off]" className="px-4 py-2 w-full border border-gray-300 rounded-md" value={formData.particularsOfParents?.FatherTelOff || ""} readOnly />
              </div>
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Mobile:</label>
              <input type="text" placeholder="[Mob]" className="mt-2 px-4 py-2 w-full border border-gray-300 rounded-md" value={formData.particularsOfParents?.FatherMobile || ""} readOnly />
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Email Address:</label>
              <input
                type="email"
                value={formData.particularsOfParents?.FatherEmail || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4 text-[#16bab1]">Mother / Guardian</h2>
          <div className="grid grid-cols-2 gap-4 items-start">
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Name:</label>
              <input
                type="text"
                value={formData.particularsOfParents?.MotherName || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Educational Qualification:</label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfParents?.MotherQualification || 'N/A'}
              </div>
            </div>
            <div className="col-span-4 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Occupation:</label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.particularsOfParents?.MotherOccupation || 'N/A'}
              </div>
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Name of the Organisation:</label>
              <input
                type="text"
                value={formData.particularsOfParents?.MotherOrganisationName || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
            <div className="col-span-4 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Tel No.:</label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <input type="text" placeholder="[STD]" className="px-4 py-2 border border-gray-300 rounded-md w-full" value={formData.particularsOfParents?.MotherSTDCodeRes || ""} readOnly />
                <input type="text" placeholder="[Res]" className="px-4 py-2 border border-gray-300 rounded-md w-full" value={formData.particularsOfParents?.MotherTelRes || ""} readOnly />
                <input type="text" placeholder="[STD]" className="px-4 py-2 w-full border border-gray-300 rounded-md" value={formData.particularsOfParents?.MotherSTDCodeOff || ""} readOnly />
                <input type="text" placeholder="[Off]" className="px-4 py-2 w-full border border-gray-300 rounded-md" value={formData.particularsOfParents?.MotherTelOff || ""} readOnly />
              </div>
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Mobile:</label>
              <input type="text" placeholder="[Mob]" className="mt-2 px-4 py-2 w-full border border-gray-300 rounded-md" value={formData.particularsOfParents?.MotherMobile || ""} readOnly />
            </div>
            <div className="col-span-4 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Email Address:</label>
              <input
                type="email"
                value={formData.particularsOfParents?.MotherEmail || ''}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Child's Personal Background */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-6">
          <h2 className="text-2xl font-semibold mb-4 text-[#16bab1] col-span-3">Child's Personal Background</h2>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please select the characteristics that generally describe your child:
            </label>
            <div className="flex flex-wrap gap-2">
              {formData.childPersonalBackground?.characteristics?.map((char, index) => (
                <span key={index} className="bg-[#16bab1] text-white px-3 py-1 rounded-full text-sm">
                  {char}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-4 md:col-span-1 col-span-2">
            <label className="block text-sm font-medium text-gray-700">Previous Schooling</label>
            <div
              className={`capitalize px-4 py-2 rounded-full border bg-[#16bab1] text-white`}
            >
              {formData.childPersonalBackground?.PreviousSchooling || 'N/A'}
            </div>
          </div>

          {formData.childPersonalBackground?.PreviousSchooling === "Yes" && (
            <div className="mb-4 md:col-span-1 col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                If yes, which year:
              </label>
              <input
                type="text"
                value={formData.childPersonalBackground?.PreviousSchoolingDetails || ''}
                className="mt-2 px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
          )}

          <div className="mb-4 md:col-span-1 col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              • Is your child Toilet trained?
            </label>
            <div
              className={`capitalize px-4 py-2 rounded-full border bg-[#16bab1] text-white`}
            >
              {formData.childPersonalBackground?.Toilettrained || 'N/A'}
            </div>
          </div>

          <div className="col-span-3 gap-4">
            <label className="block text-sm font-medium text-gray-700">
              • How many siblings does the child have?
            </label>
            <div className="col-span-3 mt-2">
              <label className="block text-sm font-medium text-gray-700">
                Brothers (mention age):
              </label>
              {formData.childPersonalBackground?.Siblings?.Brothers?.map((sibling, index) => (
                <div key={`brother-${index}`} className="flex gap-4 mt-2">
                  <input
                    type="text"
                    value={sibling.name || ""}
                    className="px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                  <input
                    type="text"
                    value={sibling.age || ""}
                    className="px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                </div>
              ))}
            </div>

            <div className="col-span-3 mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Sisters (mention age):
              </label>
              {formData.childPersonalBackground?.Siblings?.Sisters?.map((sibling, index) => (
                <div key={`sister-${index}`} className="flex gap-4 mt-2">
                  <input
                    type="text"
                    value={sibling.name || ""}
                    className="px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                  <input
                    type="text"
                    value={sibling.age || ""}
                    className="px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4 col-span-3">
            <label className="block text-sm font-medium text-gray-700">
              Has any of the child’s kin attended Kalam Kids before?
            </label>
            <div
              className={`capitalize px-4 py-2 rounded-full border bg-[#16bab1] text-white`}
            >
              {formData.childPersonalBackground?.PreviousKalamKids || 'N/A'}
            </div>
          </div>

          {formData.childPersonalBackground?.PreviousKalamKids === "Yes" && (
            <div className="mb-4 col-span-3">
              <label className="block text-sm font-medium text-gray-700">
                If yes, which year:
              </label>
              <input
                type="text"
                value={formData.childPersonalBackground?.PreviousKalamKidsDetails || ''}
                className="mt-2 px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
          )}
        </div>

        {/* Medical Record */}
        <div className="mt-6">
          <h2 className="text-2xl font-semibold mb-4 text-[#16bab1]">Medical Record</h2>
          <h3>Vaccination Details</h3>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-[#16bab1] text-white font-semibold">
                  <th className="px-4 py-2 border text-left">S.No.</th>
                  <th className="px-4 py-2 border text-left">Vaccination</th>
                  <th className="px-4 py-2 border text-left">Age</th>
                  <th className="px-4 py-2 border text-center">Yes / No</th>
                </tr>
              </thead>
              <tbody className="font-medium">
                {[
                  { vaccine: "BCG", age: "0-2 weeks" },
                  { vaccine: "DPT (I, II, III)", age: "6-24 weeks" },
                  { vaccine: "Oral Polio vaccine (OPV)", age: "6 doses" },
                  { vaccine: "Measles", age: "8-9 months" },
                  { vaccine: "MMR", age: "15-18 months" },
                  { vaccine: "DT", age: "4-6 years" },
                  { vaccine: "HBV-Hepatitis (I, II, III)", age: "Up to 24 months" },
                  { vaccine: "Hi B (Meningitis-3 doses)", age: "Under 1 year" },
                  { vaccine: "Chickenpox", age: "After 1 year of age" },
                  { vaccine: "Typhoid", age: "After 2 years" },
                  { vaccine: "Hepatitis A (2 doses)", age: "After 1 year of age" },
                ].map((vaccination, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="px-4 py-2 border text-center">{index + 1}</td>
                    <td className="px-4 py-2 border">{vaccination.vaccine}</td>
                    <td className="px-4 py-2 border">{vaccination.age}</td>
                    <td className="px-4 py-2 border text-center">
                      <div
                        className={`capitalize px-4 py-1 rounded-full border ${formData.medicalRecord?.[vaccination.vaccine] === "Yes"
                            ? "bg-[#16bab1] text-white"
                            : "bg-white text-gray-700 border-gray-300"
                          }`}
                      >
                        {formData.medicalRecord?.[vaccination.vaccine] || 'N/A'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-sm">
            <p><strong className="text-black font-semibold">Note:</strong></p>
            <ul className="list-disc list-inside ml-4 font-medium">
              <li className="text-gray-600">Vaccines (1) to (7) are compulsory.</li>
              <li className="text-gray-600">Vaccines (8) to (11) are optional but recommended.</li>
            </ul>
          </div>
          
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-700">
              II. History of Past Illness
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Specific ailments suffered in the past:
                </label>
                <input
                  type="text"
                  value={formData.medicalRecord?.suffered || ""}
                  className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Surgery undergone (if any):
                </label>
                <input
                  type="text"
                  value={formData.medicalRecord?.surgery || ""}
                  className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Allergy (if any):
                </label>
                <input
                  type="text"
                  value={formData.medicalRecord?.allergy || ""}
                  className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                  readOnly
                />
              </div>
            </div>
          
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700">
                Does your child suffer from any phobias?
              </label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.medicalRecord?.phobias || 'N/A'}
              </div>
              {formData.medicalRecord?.phobias === "Yes" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Please specify:
                  </label>
                  <input
                    type="text"
                    value={formData.medicalRecord?.phobiasDetails || ""}
                    className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                </div>
              )}
            </div>
          
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700">
                Is the child presently on any regular medication?
              </label>
              <div
                className={`capitalize px-4 py-1 rounded-full border bg-[#16bab1] text-white`}
              >
                {formData.medicalRecord?.medication || 'N/A'}
              </div>
              {formData.medicalRecord?.medication === "Yes" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Please specify:
                  </label>
                  <input
                    type="text"
                    value={formData.medicalRecord?.medicationDetails || ""}
                    className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                    readOnly
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Declaration Section */}
        <div className="max-w-6xl mx-auto bg-white p-2 mt-3 rounded-lg shadow-md border border-[#16bab1]">
          <h2 className="text-md py-2 font-semibold text-center bg-[#16bab1] text-white mb-4">
            Declaration of parent / Guardian
          </h2>
          <div className="mt-6">
            <p className="text-md font-medium text-gray-700 mb-6 leading-relaxed">
              I declare that the information given is correct and complete and I have not withheld any information. I agree to entrust my child under the care of the staff at{" "}
              <span className="font-semibold">Kalam Kids</span>. I am aware that the fee once paid is{" "}
              <span className="font-semibold">non-transferable</span> and <span className="font-semibold">non-refundable</span> under any circumstances. I have read through the{" "}
              <span className="font-semibold">Kalam Kids</span> pre-school policies and am in agreement with the said policies.
            </p>

            {/* Signature Field */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent's Signature:
              </label>
              {formData.Declarationparent?.signatureRef && (
                <img
                  src={formData.Declarationparent.signatureRef}
                  alt="Parent's Signature"
                  className="border border-gray-300 rounded-md"
                  style={{ width: '100%', height: 'auto' }}
                />
              )}
            </div>

            {/* Date Field */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date:
              </label>
              <input
                type="date"
                value={formData.Declarationparent?.date || ""}
                className="mt-2 block px-4 py-2 w-full border border-gray-300 rounded-md"
                readOnly
              />
            </div>
          </div>

          {/* Acknowledgment Section */}
          <div className="mt-8">
            <h2 className="text-md py-2 font-semibold text-center bg-[#16bab1] text-white mb-4">
              Acknowledgment of Parental Responsibility
            </h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              I,{" "}
              <input
                type="text"
                value={formData.Declarationparent?.acknowledgmentName || ""}
                className="border-b border-gray-400 focus:border-[#16bab1] px-2 py-1 outline-none"
                readOnly
              />
              , hereby acknowledge that I understand the expectations to participate in the smooth running of my children’s Play School as follows:
            </p>
            <div className="text-sm text-gray-600 mb-6">
              <label className="block mb-4">
                <input
                  type="checkbox"
                  name="volunteerJob"
                  className="mr-2"
                  checked={formData.Declarationparent?.volunteerJob || false}
                  readOnly
                  disabled // Disable interaction for printing
                />
                I am required to take at least one job from the Volunteer Job list and perform it to the best of my ability.
              </label>
              <label className="block mb-4">
                <input
                  type="checkbox"
                  name="specialEventsCost"
                  className="mr-2"
                  checked={formData.Declarationparent?.specialEventsCost || false}
                  readOnly
                  disabled // Disable interaction for printing
                />
                I am required to pay for my children’s special events and field trip costs.
              </label>
              <label className="block mb-4">
                <input
                  type="checkbox"
                  name="attendMeetings"
                  className="mr-2"
                  checked={formData.Declarationparent?.attendMeetings || false}
                  readOnly
                  disabled // Disable interaction for printing
                />
                I am required to attend Annual General Meetings.
              </label>
            </div>

            {/* Acknowledgment Signature Field */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Acknowledgment Signature:
              </label>
              {formData.Declarationparent?.acknowledgmentSignatureRef && (
                <img
                  src={formData.Declarationparent.acknowledgmentSignatureRef}
                  alt="Acknowledgment Signature"
                  className="border border-gray-300 rounded-md"
                  style={{ width: '100%', height: 'auto' }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintSingleForm;